/* eslint-disable react/jsx-sort-props */
//
// QC #33 — major rewrite. Original page:
//   - Only loaded if ?worker_id= or ?factory_id= was in URL (else empty).
//   - Worker ID was a free-text input (users pasted names → BE 400).
//   - createMutation had no onError → silent failure.
//   - Custom envelope cast forgot the `.data` wrapper.
//
// New flow:
//   - Uses /violation/filter (generic dispatcher we added in BE) so the
//     page always renders an entry table.
//   - Worker is a Select dropdown sourced from /worker/filter (search).
//   - Toast on mutation error so QC + KH see exactly what BE rejects.
//   - Table renders worker name (joined client-side from same hook).
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Table,
} from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { axiosClient } from '@/api/axiosClient';
import { useGetWorkerList } from '@/modules/worker/hooks/useGetWorkerList';

interface ViolationRow {
  id: string;
  worker_id: string;
  factory_id?: string;
  group_code: string;
  occurred_at: string;
  severity: number;
  note?: string;
}

interface MasterValue {
  id: string;
  parent_id: string;
  value: string;
}

interface WorkerLite {
  id: string;
  full_name?: string;
  phone?: string;
  code?: string;
}

const ViolationPage = () => {
  const [params] = useSearchParams();
  const workerIdFromUrl = params.get('worker_id') || '';
  const factoryIdFromUrl = params.get('factory_id') || '';
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // BE /violation/filter (we added this in BE sprint-2) — works with no
  // params (returns empty page) or with worker_id / factory_id scope.
  const qs = new URLSearchParams();
  if (workerIdFromUrl) qs.set('worker_id', workerIdFromUrl);
  if (factoryIdFromUrl) qs.set('factory_id', factoryIdFromUrl);
  const endpoint = `/violation/filter${qs.toString() ? '?' + qs.toString() : ''}`;

  const { data, isLoading } = useQuery(
    ['violations', workerIdFromUrl, factoryIdFromUrl],
    () => axiosClient.get<{ rows: ViolationRow[]; total_rows: number }>(endpoint),
    { refetchOnWindowFocus: false }
  );
  const rows =
    (data as unknown as { data?: { rows: ViolationRow[] } })?.data?.rows || [];

  // Master data for "Nhóm lỗi" — NHOM-LOI-VI-PHAM has 12 values per seed.
  const { data: groups } = useQuery(
    ['md-violation-groups'],
    () =>
      axiosClient.get<{ data: MasterValue[] }>(
        '/master-data/list?parent_id=NHOM-LOI-VI-PHAM'
      ),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );
  // /master-data/list returns the full envelope; the parent we want is in
  // data[].data (each parent has its own children array).
  const groupOptions = useMemo(() => {
    type Parent = { id: string; data?: MasterValue[] };
    const parents = (groups as unknown as { data?: Parent[] })?.data ?? [];
    const target = parents.find((p) => p.id === 'NHOM-LOI-VI-PHAM');

    return (target?.data ?? []).map((g) => ({ label: g.value, value: g.id }));
  }, [groups]);

  // Worker dropdown options. Cap at 200 for the picker; user can search by
  // typing (antd showSearch + optionFilterProp).
  const { data: workersResp } = useGetWorkerList('page_size=200&page_index=1');
  const workers = (workersResp?.rows as WorkerLite[]) ?? [];
  const workerOptions = workers
    .filter((w) => w.id)
    .map((w) => ({
      label:
        (w.full_name || '(no name)') +
        (w.code ? ` [${w.code}]` : '') +
        (w.phone ? ' • ' + w.phone : ''),
      value: w.id,
    }));
  const workerNameById = useMemo(() => {
    const m: Record<string, string> = {};
    workers.forEach((w) => {
      if (w.id) m[w.id] = w.full_name || w.id.slice(0, 8);
    });

    return m;
  }, [workers]);

  const createMutation = useMutation(
    (payload: Record<string, unknown>) => axiosClient.post('/violation/create', payload),
    {
      onError: (err: unknown) => {
        const m = (err as { message?: string })?.message ?? 'Tạo vi phạm thất bại';
        message.error(m);
      },
      onSuccess: () => {
        message.success('Đã ghi nhận vi phạm');
        setModalOpen(false);
        form.resetFields();
        queryClient.invalidateQueries(['violations']);
      },
    }
  );

  const columns = [
    {
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      render: (v: string) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-'),
      title: 'Thời điểm',
      width: 160,
    },
    {
      dataIndex: 'worker_id',
      key: 'worker',
      render: (id: string) => workerNameById[id] || id.slice(0, 8) + '…',
      title: 'NLĐ',
    },
    { dataIndex: 'group_code', key: 'group', title: 'Nhóm lỗi' },
    {
      dataIndex: 'severity',
      key: 'severity',
      render: (v: number) => '⚠️'.repeat(v),
      title: 'Mức',
      width: 100,
    },
    { dataIndex: 'note', key: 'note', title: 'Ghi chú' },
  ];

  return (
    <Card
      extra={
        <Button icon={<PlusOutlined rev="" />} onClick={() => setModalOpen(true)} type="primary">
          Thêm vi phạm
        </Button>
      }
      title={
        workerIdFromUrl
          ? `Vi phạm NLĐ ${workerNameById[workerIdFromUrl] || workerIdFromUrl.slice(0, 8) + '…'}`
          : factoryIdFromUrl
            ? `Vi phạm theo Nhà máy ${factoryIdFromUrl.slice(0, 8)}…`
            : 'Lỗi vi phạm NLĐ'
      }
    >
      <Table<ViolationRow>
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        rowKey="id"
        size="middle"
      />

      <Modal
        cancelText="Huỷ"
        confirmLoading={createMutation.isLoading}
        okText="Lưu"
        onCancel={() => setModalOpen(false)}
        onOk={async () => {
          const values = await form.validateFields();
          createMutation.mutate({
            ...values,
            factory_id: factoryIdFromUrl || values.factory_id || undefined,
            occurred_at: values.occurred_at?.toISOString?.(),
            worker_id: workerIdFromUrl || values.worker_id,
          });
        }}
        open={modalOpen}
        title="Ghi nhận vi phạm mới"
      >
        <Form form={form} layout="vertical">
          {!workerIdFromUrl && (
            <Form.Item
              label="NLĐ"
              name="worker_id"
              rules={[{ message: 'Bắt buộc', required: true }]}
            >
              <Select
                optionFilterProp="label"
                options={workerOptions}
                placeholder="Tìm NLĐ theo tên / mã / SĐT..."
                showSearch
              />
            </Form.Item>
          )}
          <Form.Item
            label="Nhóm lỗi"
            name="group_code"
            rules={[{ message: 'Bắt buộc', required: true }]}
          >
            <Select options={groupOptions} placeholder="Chọn nhóm lỗi..." />
          </Form.Item>
          <Form.Item initialValue={1} label="Mức độ (1-5)" name="severity">
            <Select
              options={[1, 2, 3, 4, 5].map((n) => ({ label: '⚠️'.repeat(n), value: n }))}
            />
          </Form.Item>
          <Form.Item
            initialValue={moment()}
            label="Thời điểm xảy ra"
            name="occurred_at"
            rules={[{ message: 'Bắt buộc', required: true }]}
          >
            <DatePicker format="DD/MM/YYYY HH:mm" showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Ghi chú" name="note">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ViolationPage;
